import { Button } from '@react-apps/design-system';
import type { QueueCounts, QueueItem } from '../hooks/use-request-queue';
import type { RequestFailure } from '../display';
import { SectionCard } from '../display';
import { ImportIssueCard } from './ImportIssueCard';

/**
 * Progresso da fila em **uma barra**, e o que falhou num bloco de pendências no
 * topo.
 *
 * A primeira versão eram três listas lado a lado ("Processando" / "Concluído" /
 * "Aguardando"), literais ao pedido. O problema não era estético: o item que
 * falha entra na coluna "Concluído" (ele terminou, só terminou mal), dentro de
 * uma lista com scroll próprio — numa prova de 80 questões, a falha da questão
 * 14 ficava atrás de ~60 itens de scroll interno, e as outras duas colunas
 * passavam a maior parte do tempo vazias. Erro tem de vir no fim do processo,
 * junto, e visível inteiro.
 *
 * O painel continua genérico: não sabe o que é importação, nem por que um item
 * falhou — só o que fazer a respeito, que `classifyRequestError` já respondeu.
 *
 * Não há countdown nem "tentando de novo em 3s": a fila não reenvia sozinha
 * (ver `useRequestQueue`). O que falhou fica parado, com o erro do servidor
 * inteiro na tela, e o reenvio é o botão.
 */
export function RequestQueuePanel({
  items,
  counts,
  onRetry,
  title = 'Fila de requisições',
  doneLabel = 'concluídas',
}: {
  items: QueueItem[];
  counts: QueueCounts;
  onRetry: (id: string) => void;
  title?: string;
  /** Particípio do que a fila faz: "importadas", "excluídas", "concluídas". */
  doneLabel?: string;
}) {
  const pending = items.filter((item) => item.status === 'failed');

  if (items.length === 0) return null;

  const total = items.length;
  const running = counts.queued + counts.processing > 0;
  const processing = items.filter((item) => item.status === 'processing');

  return (
    <SectionCard
      title={title}
      action={
        <span className="text-(--color-text-secondary) text-xs">
          {running
            ? `${counts.done} de ${total} · ${counts.processing} processando`
            : `${counts.done} ${doneLabel}`}
          {pending.length > 0 && ` · ${pending.length} precisam de atenção`}
        </span>
      }
    >
      <div className="space-y-4">
        <ProgressBar counts={counts} total={total} />

        {pending.length > 0 && (
          <div className="space-y-2">
            {pending.map((item) => (
              <ImportIssueCard
                key={item.id}
                title={item.label}
                badge={badgeLabel(item.failure)}
                badgeVariant="error"
                message={item.failure?.message ?? 'falha na requisição'}
                detail={detailFor(item)}
                actions={
                  // Reenviar só faz sentido em falha transitória. Em 4xx o
                  // botão seria ruído: o mesmo payload dá o mesmo erro.
                  item.failure?.kind === 'transient' && (
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => onRetry(item.id)}
                    >
                      Tentar de novo
                    </Button>
                  )
                }
              />
            ))}
          </div>
        )}

        {processing.length > 0 && (
          <p className="text-(--color-text-secondary) text-xs">
            processando: {processing.map((item) => item.label).join(', ')}
            {counts.queued > 0 && ` · ${counts.queued} na fila`}
          </p>
        )}

        {counts.done > 0 && (
          // `<details>` nativo: o histórico continua acessível sem estado novo e
          // sem disputar espaço com o que falhou.
          <details>
            <summary className="text-(--color-text-secondary) hover:text-(--color-text-primary) cursor-pointer text-xs">
              {counts.done} {doneLabel} · ver lista
            </summary>
            <ul className="mt-2 grid grid-cols-2 gap-x-4 sm:grid-cols-3">
              {items
                .filter((item) => item.status === 'done')
                .map((item) => (
                  <li
                    key={item.id}
                    className="text-(--color-text-secondary) text-xs"
                  >
                    ✓ {item.label}
                  </li>
                ))}
            </ul>
          </details>
        )}
      </div>
    </SectionCard>
  );
}

function ProgressBar({
  counts,
  total,
}: {
  counts: QueueCounts;
  total: number;
}) {
  const pct = (value: number) => `${(value / total) * 100}%`;
  const finished = counts.done + counts.failed;

  return (
    // Não existe ProgressBar no design-system — `div` é o caminho, com os
    // atributos de acessibilidade no lugar do componente que não há.
    <div
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={total}
      aria-valuenow={finished}
      aria-label={`${counts.done} de ${total} concluídas`}
      className="bg-(--color-border) flex h-2 w-full overflow-hidden rounded-full"
    >
      <div className="bg-green-500" style={{ width: pct(counts.done) }} />
      <div className="bg-red-500" style={{ width: pct(counts.failed) }} />
    </div>
  );
}

function badgeLabel(failure: RequestFailure | undefined): string {
  if (!failure) return 'falha';
  if (failure.status === undefined) return 'falha de rede';
  return `erro ${failure.status}`;
}

function detailFor(item: QueueItem): string | undefined {
  if (item.failure?.kind === 'permanent') {
    return 'Repetir sem corrigir daria o mesmo erro.';
  }
  // Na primeira falha o botão ao lado já diz o que fazer; repetir isso em texto
  // seria legenda de botão. A partir da segunda o número é a única coisa que
  // diferencia o card de antes do clique — sem ele, reenviar e falhar de novo
  // parece não ter feito nada.
  return item.attempt > 1
    ? `Falhou de novo — ${item.attempt}ª tentativa.`
    : undefined;
}
